import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { minWidth } from '@mui/system'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import { addItemToUpdateQueue } from '@/app/slices/orderItemsSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'

const classes = {
    formControl_small: {
        width: '100px',
    },
    formControl_select: {
        minWidth: '100px',
    },
}

const CustomPropertySecProcess = ({
    isPartFromLibrary = false,
    onInputChange,
    orderItemId,
    process,
    showLabel = true,
    sx = null,
}) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const [propertyValue, setPropertyValue] = useState(process.customValue || '')
    const [propertyError, setPropertyError] = useState(!process?.customValue)

    const { $type, ...rest } = process

    const debounceOnInputChange = useDebouncedCallback(onInputChange, 1000)

    const handleInputChange = async (event) => {
        const value = event.target.value

        debounceOnInputChange.cancel()
        dispatch(addItemToUpdateQueue(orderItemId))

        setPropertyError(value === '')
        setPropertyValue(value)

        if (value) {
            debounceOnInputChange({ ...rest, isEnabled: true, customValue: value })
        }
    }

    return (
        <TbxTooltip
            title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
            arrow
        >
            {process.customPropertyOptions?.length > 0 ? (
                <FormControl
                    color="primary"
                    error={propertyError}
                    size="small"
                    sx={[classes.formControl_select, ...(Array.isArray(sx) ? sx : [sx])]}
                    variant="outlined"
                >
                    {showLabel ? (
                        <InputLabel
                            htmlFor="select-customProperty"
                            shrink
                        >
                            {t(process.customPropertyName)}
                        </InputLabel>
                    ) : null}
                    <Select
                        data-testid="order-item-source"
                        disabled={isPartFromLibrary}
                        id="select-customProperty"
                        label={showLabel ? t(process.customPropertyName) : null}
                        labelId="select-customProperty-label"
                        sx={{
                            '& .MuiSelect-select': {
                                fontSize: '14px',
                                paddingBlock: '5px',
                                paddingInline: '12px',
                            },
                        }}
                        value={propertyValue}
                        displayEmpty
                        onChange={handleInputChange}
                    >
                        <MenuItem value="">
                            <span>{t('Select option')}</span>
                        </MenuItem>
                        {process.customPropertyOptions.map((option) => (
                            <MenuItem
                                key={option}
                                value={option}
                            >
                                {t(option)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ) : (
                <TextField
                    disabled={isPartFromLibrary}
                    error={propertyError}
                    label={showLabel ? process.customPropertyName : null}
                    size="small"
                    slotProps={{
                        inputLabel: {
                            shrink: true,
                        },
                        htmlInput: {
                            style: { textAlign: 'right', paddingBlock: '6px', paddingInline: '12px' },
                            'data-testid': 'order-item-secondary-process-input',
                        },
                        input: {
                            style: { fontSize: '14px' },
                        },
                    }}
                    sx={[classes.formControl_small, ...(Array.isArray(sx) ? sx : [sx])]}
                    value={propertyValue}
                    variant="outlined"
                    onBlur={handleInputChange}
                    onChange={handleInputChange}
                />
            )}
        </TbxTooltip>
    )
}

CustomPropertySecProcess.propTypes = {
    isPartFromLibrary: PropTypes.bool,
    orderItemId: PropTypes.string,
    process: PropTypes.object,
    showLabel: PropTypes.bool,
    sx: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
        PropTypes.func,
        PropTypes.object,
    ]),
    onInputChange: PropTypes.func,
}

export default CustomPropertySecProcess
